import { Injectable, OnDestroy } from '@angular/core'
import { ImageService as ImageApiService, ImagePath } from '@sciper/api-client'
import imageCompression from 'browser-image-compression'

import { Subject, firstValueFrom, takeUntil } from 'rxjs'
import {
  AbstractRestService,
  RestServiceStatus,
} from '../abstract-rest/abstract-rest.service'

const MAX_IMAGE_SIZE_MB = 2
const MAX_IMAGE_WIDTH_HEIGHT = 1920

@Injectable({
  providedIn: 'root',
})
export class ImageService extends AbstractRestService implements OnDestroy {
  private _destroySubject$: Subject<void> = new Subject<void>()

  constructor(private imageApiService: ImageApiService) {
    super()
  }

  override ngOnDestroy() {
    super.ngOnDestroy()
    this._destroySubject$.next()
    this._destroySubject$.complete()
  }

  async createImage(
    path: string,
    file: File,
    callback:
      | ((success: boolean, imagePath?: ImagePath) => void)
      | undefined = undefined
  ): Promise<ImagePath | undefined> {
    if (!path || !file) {
      if (callback) {
        callback(false, undefined)
      }
      return Promise.resolve(undefined)
    }

    this.status.next(RestServiceStatus.Loading)

    try {
      const compressedImage = await this.compressImage(file)
      const imagePath = await firstValueFrom(
        this.imageApiService
          .createImage(path, compressedImage)
          .pipe(takeUntil(this._destroySubject$))
      )

      this.status.next(RestServiceStatus.Success)

      if (callback) {
        callback(true, imagePath)
      }

      return imagePath
    } catch (error) {
      if (this.isNetworkError(error)) {
        this.status.next(RestServiceStatus.NetworkError)
      } else {
        this.status.next(RestServiceStatus.Error)
      }

      if (callback) {
        callback(false, undefined)
      }

      return undefined
    }
  }

  createFileFromPhoto(base64: string, format: string, fileName: string): File {
    const contentType = `image/${format}`
    return this.base64ToFile(base64, contentType, fileName)
  }

  private base64ToFile(
    base64Data: string,
    contentType = '',
    fileName: string,
    sliceSize = 512
  ): File {
    const byteCharacters = atob(base64Data)
    const byteArrays: Uint8Array[] = []

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize)
      const byteNumbers = new Array(slice.length)
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      byteArrays.push(byteArray)
    }

    const blob = new Blob(byteArrays, { type: contentType })
    return new File([blob], fileName, { type: blob.type })
  }

  private async compressImage(file: File): Promise<File> {
    const options = {
      maxSizeMB: MAX_IMAGE_SIZE_MB,
      maxWidthOrHeight: MAX_IMAGE_WIDTH_HEIGHT,
      useWebWorker: true,
    }

    const compressedFile = await imageCompression(file, options)
    return compressedFile
  }
}
